import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { DownloadFile } from 'components/common/download-files/DownloadFilesTable';
import { FileInfo } from 'types/policyService';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { useRootStore } from 'store/rootStore';
import ActionModal from 'components/common/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { toJS } from 'mobx';
import { useProductOutputsPageStore } from 'store/pages/product-outputs/pageStore';
import ZipFileSection from 'components/common/download-files/ZipFileSection';
import TableInfoBar from 'components/common/download-files/TableInfoBar';
import { OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { ICustomizableElement } from '@insurancemenu/imenu-components-lib/dist/types';
import { DownloadFilesTableStyled } from './styles';

export interface FilesTableProps extends ICustomizableElement {
  title: string;
  isLoading: boolean;
  files: FileInfo[];
  zip: FileInfo | null;
}

const FilesTable: React.FC<FilesTableProps> = observer(({ title, files, isLoading, zip, className }) => {
  const convertFiles = (filesList: FileInfo[]): DownloadFile[] =>
    filesList.map((file) => ({
      id: file.link,
      name: file.name,
    }));

  const convertedZip = zip ? { id: `${zip.type}_${zip.name}`, name: zip.name } : undefined;

  const downloadZip = (): void => {
    if (zip && zip.link) {
      downloadByUrl(zip?.link);
    }
  };

  const downloadFile = (link: string): void => {
    downloadByUrl(link);
  };

  return (
    <>
      <TableInfoBar title={title} totalFiles={files.length} />
      <ZipFileSection file={convertedZip} loading={isLoading} onDownload={downloadZip} />
      <DownloadFilesTableStyled
        loading={isLoading}
        filesList={convertFiles(files)}
        onDownload={downloadFile}
        className={className}
      />
    </>
  );
});

const BenefitsTable = observer((): JSX.Element => {
  const store = useProductOutputsPageStore();

  // Mobx store
  const {
    isWarningModalOpen,
    isLoading,
    benefitSummaries,
    productsJson,
    benefitsZip,
    jsonZip,
    getInitialData,
    resetInitialData,
    setWarningModal,
  } = store;

  const {
    userStore: { userRoles },
  } = useRootStore();

  const { periodId } = useParams<{ periodId: string }>();

  useEffect(() => {
    void getInitialData(periodId);
    return resetInitialData;
  }, []);

  const closeWarningModal = (): void => {
    setWarningModal(false);
  };

  return (
    <>
      {isLoading ? (
        <OverlaySpinner display={isLoading} overlay="none" />
      ) : (
        <>
          <FilesTable
            title="Benefits Summaries"
            isLoading={isLoading}
            files={benefitSummaries}
            zip={toJS(benefitsZip)}
          />
          {celticsRights(userRoles) && (
            <FilesTable title="Products JSON" isLoading={isLoading} files={productsJson} zip={toJS(jsonZip)} />
          )}
          <ActionModal
            show={isWarningModalOpen}
            onClose={closeWarningModal}
            title={
              <span>
                Proposal output files are currently being generated. <br /> Please check back in a minute.
              </span>
            }
            modalType={ModalType.NOTIFICATION}
          />
        </>
      )}
    </>
  );
});

export default BenefitsTable;
